import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Chip,
  Icons,
  Tooltip,
  Typography,
  useTheme,
} from '@ublend-npm/aulaui-core';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { STUDENT_ROLE } from '@core/constants/space';
import { changeClassRoom } from '@core/actions/classRoom';
import { HomeViewRole } from '@app/constants/home';
import { HomeUpcomingAssignment } from '@core/assignments/api/homeUpcomingAssignments';
import push from '@app/utils/routing';
import useIsClamped from '@app/hooks/useIsClamped';
import useIsOverflowing from '@app/hooks/useIsOverflowing';

const STRINGS = {
  dueDate: 'Due Date',
  noDueDate: 'No due date',
  submitted: 'Submitted',
};

export const formatAssignmentDueDate = (dueDate: string | null) => {
  if (!dueDate) {
    return STRINGS.noDueDate;
  }

  const date = new Date(dueDate);

  if (Number.isNaN(date.getTime())) {
    return STRINGS.noDueDate;
  }

  return date.toLocaleDateString('en-GB');
};

type TruncatedTextProps = {
  text: string;
  isTruncated: boolean;
  setRef: (node: HTMLDivElement | null) => void;
  sx?: Record<string, unknown>;
  sxText?: Record<string, unknown>;
};

const TruncatedText = ({
  text,
  isTruncated,
  setRef,
  sx,
  sxText,
}: TruncatedTextProps) => {
  const content = (
    <Box
      ref={setRef}
      tabIndex={0}
      aria-label={text}
      sx={{
        minWidth: 0,
        overflow: 'hidden',
        ...sx,
      }}
    >
      <Typography sx={{ ...sxText }}>{text}</Typography>
    </Box>
  );

  if (!isTruncated) {
    return content;
  }

  return (
    <Tooltip title={text} placement="top" arrow>
      {content}
    </Tooltip>
  );
};

type UpcomingAssignmentRowProps = {
  assignment: HomeUpcomingAssignment;
  viewRole: HomeViewRole;
  termLabel?: string | null;
  showDivider?: boolean;
};

const UpcomingAssignmentRow = ({
  assignment,
  viewRole,
  termLabel = null,
  showDivider = true,
}: UpcomingAssignmentRowProps) => {
  const { palette } = useTheme();
  const dispatch = useDispatch();
  const formattedDueDate = formatAssignmentDueDate(assignment.dueDate);
  const [titleRef, isTitleClamped] = useIsClamped<HTMLDivElement>(
    assignment.title
  );
  const [courseNameRef, isCourseNameOverflowing] =
    useIsOverflowing<HTMLDivElement>();
  const canOpenAssignment =
    !assignment.cta.disabled && Boolean(assignment.cta.path);

  const handleRowClick = useCallback(async () => {
    if (!canOpenAssignment || !assignment.cta.path) {
      return;
    }

    await dispatch(changeClassRoom(assignment.spaceId) as any);
    push(assignment.cta.path);
  }, [assignment.cta.path, assignment.spaceId, canOpenAssignment, dispatch]);

  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleRowClick();
      }
    },
    [handleRowClick]
  );

  const statusIndicator =
    assignment.isHidden && viewRole !== STUDENT_ROLE ? (
      <VisibilityOffOutlinedIcon
        tabIndex={0}
        sx={{
          fontSize: 20,
          color: palette.text.disabled,
          flexShrink: 0,
        }}
        aria-label="Hidden assignment"
      />
    ) : (
      <Box
        role="status"
        aria-label={assignment.status}
        tabIndex={0}
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor:
            assignment.status === 'DueSoon'
              ? palette.warning.light
              : palette.info.light,
          flexShrink: 0,
        }}
      />
    );

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={2}
      tabIndex={0}
      sx={{
        borderBottom: showDivider ? `1px solid ${palette.divider}` : 'none',
        mb: showDivider ? '12px' : 0,
      }}
      aria-label=""
    >
      <Box
        display="flex"
        alignItems="center"
        gap={2}
        role={canOpenAssignment ? 'button' : undefined}
        tabIndex={canOpenAssignment ? 0 : undefined}
        onClick={canOpenAssignment ? handleRowClick : undefined}
        onKeyDown={canOpenAssignment ? handleRowKeyDown : undefined}
        aria-label={
          canOpenAssignment
            ? assignment.cta.label || assignment.title
            : undefined
        }
        sx={{
          minWidth: 0,
          width: '100%',
          p: '12px',
          mb: '4px',
          cursor: canOpenAssignment ? 'pointer' : 'default',
          '&:hover': {
            bgcolor: 'action.hover',
            borderRadius: '24px',
            width: '100%',
          },
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            bgcolor: palette.mode === 'dark' ? '#FFFFFF1F ' : '#0000001F',
            color: palette.text.secondary,
            flexShrink: 0,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Icons.AssignmentOutlined sx={{ fontSize: 20 }} />
        </Box>

        <Box flex={1} minWidth={0} display="flex" gap={1}>
          <Box flex={1} minWidth={0}>
            <TruncatedText
              text={assignment.title}
              isTruncated={isTitleClamped}
              setRef={titleRef}
              sx={{
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 2,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                color: 'text.primary',
                fontSize: '16px',
                fontWeight: 400,
                lineHeight: 1.25,
                letterSpacing: '0.17px',
              }}
              sxText={{
                variant: 'body2',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '150%',
                letterSpacing: '0.17px',
              }}
            />

            <Typography
              variant="caption"
              color="text.secondary"
              component="p"
              sx={{
                lineHeight: '166%',
                fontSize: '14px',
                m: 0,
                display: 'block',
              }}
              tabIndex={0}
              aria-label={STRINGS.dueDate + formattedDueDate}
            >
              {STRINGS.dueDate}: {formattedDueDate}
            </Typography>

            {assignment.courseName ? (
              <TruncatedText
                text={assignment.courseName}
                isTruncated={isCourseNameOverflowing}
                setRef={courseNameRef}
                sx={{
                  mt: '8px',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: 'text.secondary',
                  fontSize: '14px',
                  lineHeight: 1.43,
                }}
                sxText={{
                  variant: 'body2',
                  fontWeight: 400,
                  fontSize: '14px',
                  lineHeight: '150%',
                  letterSpacing: '0.17px',
                }}
              />
            ) : null}

            {termLabel ? (
              <Typography
                variant="body2"
                color="text.secondary"
                component="p"
                sx={{
                  lineHeight: 1.43,
                  m: 0,
                  display: 'flex',
                  alignItems: 'center',
                }}
                tabIndex={0}
                aria-label={termLabel}
              >
                {termLabel.split(' | ').map((part, index) => (
                  <React.Fragment key={`${part}-${index}`}>
                    {index > 0 ? (
                      <Box component="span" aria-hidden sx={{ px: '8px' }}>
                        |
                      </Box>
                    ) : null}
                    <Box component="span">{part}</Box>
                  </React.Fragment>
                ))}
              </Typography>
            ) : null}
          </Box>

          <Box
            display="flex"
            alignItems="flex-start"
            gap={0.75}
            flexShrink={0}
            sx={{ pt: '8px', pr: '8px' }}
          >
            {viewRole === STUDENT_ROLE && assignment.isSubmitted && (
              <Chip
                size="small"
                label={STRINGS.submitted}
                role="status"
                aria-label={STRINGS.submitted}
                sx={{ flexShrink: 0, m: 0 }}
              />
            )}
            {statusIndicator}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UpcomingAssignmentRow;
